import { useCallback } from 'react';
import { useWorkspaceStore, setWorkspaces, setCurrentWorkspace, setWorkspaceReloadNonce } from '../store/workspaceStore';
import { setCollections, setSchemas, setSelectedRequest, setSelectedCollection } from '../store/collectionsStore';
import { setEnvironments, setSelectedEnvironment } from '../store/variablesStore';
import { useAuthStore } from '../store/authStore';
import {
  setShowWorkspaceModal,
  setWorkspaceModalMode,
  setSelectedWorkspaceForEdit,
  setUpgradeReason,
  setUpgradeModalOpen,
} from '../store/uiModalStore';
import {
  isLocalWorkspace,
  isBackendWorkspace,
  generateLocalWorkspaceId,
  buildLocalManifestEntries,
  mergeRemoteAndLocalWorkspaces,
} from '../utils/workspaceStorage';
import { normalizeSchemas } from '../utils/schemaTree';
import { workspaceService } from '../services/workspaceService';
import {
  saveSnapshot,
  saveLocalWorkspaceManifest,
  removeLocalWorkspaceFiles,
  listLocalWorkspaceMetasResolved,
} from '../services/localWorkspaceStore';
import { removeMirror } from '../services/workspaceMirror';
import { removeWorkspaceTabs } from '../services/workspaceTabStore';
import { requireWorkspaceSlot } from '../plans/localWorkspaces';
import { useWorkspaceFork } from './useWorkspaceFork';
import { useWorkspacePublish } from './useWorkspacePublish';
import { useWorkspaceClone } from './useWorkspaceClone';
import { useWorkspaceGitActions } from './useWorkspaceGitActions';

/**
 * Workspace lifecycle handlers. Built up across brief-05 increments. The leaf
 * operations live in their own hooks (useWorkspaceGitActions, useWorkspaceClone,
 * useWorkspacePublish, useWorkspaceFork); lookups and create / edit / delete /
 * switch stay here. The big workspace-load / mirror / file-watcher / splash
 * EFFECTS stay in App and the loading-state useState stays in App (its setters
 * are passed in). All domain reads go through getState(); buildLiveSnapshot comes
 * from useSchemas.
 *
 * @param {{
 *   toastT: Function,
 *   buildLiveSnapshot: Function,
 *   snapshotHydratedRef: { current: any },
 *   setGitSyncingWorkspaceId: Function,
 *   setPublishingWorkspaceId: Function,
 *   setForkingWorkspaceId: Function,
 * }} deps
 */
export function useWorkspaces({
  toastT,
  buildLiveSnapshot,
  snapshotHydratedRef,
  setGitSyncingWorkspaceId,
  setPublishingWorkspaceId,
  setForkingWorkspaceId,
}) {
  const {
    handleGitSync,
    handleGitChanges,
    handleGitPutBack,
    handleGitItemHistory,
    handleGitPull,
    handleGitConnectConfirm,
    handleGitAddRemote,
    handleGitStopTracking,
    handleGitResolveConflicts,
  } = useWorkspaceGitActions({ buildLiveSnapshot, toastT, setGitSyncingWorkspaceId });

  const { handleWorkspaceCloned } = useWorkspaceClone({ toastT, snapshotHydratedRef });

  const {
    requestPublishLocalWorkspace,
    handlePublishLocalConfirm,
    canPublishToNetwork,
    requestPublishToNetwork,
    handleNetworkImported,
  } = useWorkspacePublish({ toastT, setPublishingWorkspaceId });

  const {
    openForkWorkspaceModal,
    handleForkWorkspaceConfirm,
  } = useWorkspaceFork({ toastT, snapshotHydratedRef, setForkingWorkspaceId });

  /* ── Lookups ───────────────────────────────────────────────────────────── */
  const getDefaultCloudWorkspace = useCallback(() => {
    const { workspaces } = useWorkspaceStore.getState();
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    return workspaces.find(
      (w) => isBackendWorkspace(w, isAuthenticated) && w.type !== 'team' && w.source !== 'team'
    );
  }, []);

  // Whether the given user can access a workspace.
  const canAccessWorkspace = useCallback((workspace, user) => {
    if (!workspace || !user) return false;

    if (workspace.storage === 'local') return true;

    const ownerId = workspace.owner_id || workspace.user_id || workspace.owner?.id;

    if (workspace.role === 'owner') return true;
    if (ownerId === user.id) return true;

    if (workspace.can_access === false) return false;
    if (workspace.can_access === true) return true;
    if (typeof workspace.can_edit === 'boolean') return true;

    if (workspace.type === 'personal') {
      return workspace.role === 'owner' || ownerId === user.id;
    }

    if (workspace.type === 'team') {
      if (workspace.role || workspace.user_role) return true;
      if (workspace.visibility === 'team' || workspace.visibility === 'public') return true;
    }

    return false;
  }, []);

  const reloadWorkspacesFromServer = useCallback(async () => {
    try {
      const manifestLocals = await listLocalWorkspaceMetasResolved();

      if (!useAuthStore.getState().isAuthenticated) {
        // No server list while signed out, but a share accept can still mint
        // a local workspace — fold new manifest entries into the store so it
        // appears without waiting for the next bootstrap.
        setWorkspaces((prev) => mergeRemoteAndLocalWorkspaces(prev, manifestLocals));
        return;
      }

      const userWorkspaces = await workspaceService.getWorkspaces();
      const allWorkspaces = Array.isArray(userWorkspaces)
        ? userWorkspaces
        : userWorkspaces?.all_workspaces || [];
      setWorkspaces(mergeRemoteAndLocalWorkspaces(allWorkspaces, manifestLocals));
    } catch (error) {
      console.error('Failed to reload workspaces:', error);
    }
  }, []);

  /* ── Create / edit / delete / switch ───────────────────────────────────── */
  const createWorkspace = async (workspaceData) => {
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    try {
      const forceLocal = !isAuthenticated || workspaceData.storage === 'local';
      // Plans v2: Free keeps only its Personal workspace on this machine.
      if (forceLocal && !requireWorkspaceSlot()) return undefined;

      if (isAuthenticated && !forceLocal) {
        const { storage, ...rest } = workspaceData;
        const newWorkspace = await workspaceService.createWorkspace(rest);
        setWorkspaces((prev) => [...prev, newWorkspace]);
        setCurrentWorkspace(newWorkspace);
        toastT('toast.workspaceCreated', 'success', { name: newWorkspace.name });
        return newWorkspace;
      }

      const newId = generateLocalWorkspaceId();
      const newWorkspace = {
        id: newId,
        local_uid: newId,
        name: workspaceData.name,
        type: workspaceData.type || 'personal',
        storage: 'local',
        source: 'personal',
        role: 'owner',
        description: workspaceData.description || '',
        visibility: workspaceData.visibility || 'private',
        settings: workspaceData.settings || {
          fork_copy_variables: workspaceData.fork_copy_variables !== false,
        },
        createdAt: Date.now(),
      };
      setWorkspaces((prev) => {
        const next = [...prev, newWorkspace];
        saveLocalWorkspaceManifest(buildLocalManifestEntries(next)).catch((err) => {
          console.error('Failed to save local workspace manifest:', err);
        });
        return next;
      });
      setCurrentWorkspace(newWorkspace);
      setEnvironments([]);
      setSelectedEnvironment(null);
      await saveSnapshot(newId, {
        collections: [],
        globalVariables: [],
        workspaceVariables: [],
        environments: [],
        selectedEnvironmentId: '',
      });
      snapshotHydratedRef.current = newId;
      toastT('toast.localWorkspaceCreated', 'success', { name: newWorkspace.name });
      return newWorkspace;
    } catch (error) {
      if (error.response?.data?.upgrade_required) {
        setUpgradeReason(error.response.data.error || '');
        setUpgradeModalOpen(true);
        return;
      }
      console.error('Failed to create workspace:', error);
      // Carry the server's explanation when there is one — a bare "Failed to
      // create workspace" hides the actual reason (quota, name conflict, …).
      const reason = error.response?.data?.error || error.response?.data?.message || '';
      if (reason) {
        toastT('toast.createWorkspaceFailedReason', 'error', { message: reason });
      } else {
        toastT('toast.createWorkspaceFailed', 'error');
      }
      throw error;
    }
  };

  const editWorkspace = async (workspaceId, updates) => {
    const { workspaces, currentWorkspace } = useWorkspaceStore.getState();
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    try {
      const existingWorkspace =
        workspaces.find((ws) => String(ws.id) === String(workspaceId)) || currentWorkspace;
      const mergedUpdates = {
        ...updates,
        settings: {
          ...(existingWorkspace?.settings || {}),
          ...(updates.settings || {}),
        },
      };

      const isLocalWs = existingWorkspace?.storage === 'local';

      if (isAuthenticated && !isLocalWs) {
        const updatedWorkspace = await workspaceService.updateWorkspace(workspaceId, mergedUpdates);
        setWorkspaces((prev) => prev.map((ws) => (ws.id === workspaceId ? updatedWorkspace : ws)));
        if (currentWorkspace?.id === workspaceId) {
          setCurrentWorkspace(updatedWorkspace);
        }
        toastT('toast.workspaceUpdated', 'success');
      } else {
        setWorkspaces((prev) =>
          prev.map((ws) =>
            String(ws.id) === String(workspaceId) ? { ...ws, ...mergedUpdates } : ws
          )
        );
        if (currentWorkspace?.id === workspaceId) {
          setCurrentWorkspace((prev) => ({ ...prev, ...mergedUpdates }));
        }
        toastT('toast.workspaceUpdated', 'success');
      }
    } catch (error) {
      console.error('Failed to update workspace:', error);
      toastT('toast.updateWorkspaceFailed', 'error');
      throw error;
    }
  };

  const deleteWorkspace = async (workspaceId) => {
    const { workspaces, currentWorkspace } = useWorkspaceStore.getState();
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const targetWs = workspaces.find((ws) => String(ws.id) === String(workspaceId));
    const isLocalWs = targetWs?.storage === 'local';

    try {
      if (isAuthenticated && targetWs && !isLocalWs) {
        await workspaceService.deleteWorkspace(workspaceId);
      }

      await removeLocalWorkspaceFiles(workspaceId);
      await removeMirror(workspaceId);
      await removeWorkspaceTabs(workspaceId);

      setWorkspaces((prev) => prev.filter((ws) => String(ws.id) !== String(workspaceId)));

      if (currentWorkspace?.id === workspaceId) {
        const remainingWorkspaces = workspaces.filter((ws) => String(ws.id) !== String(workspaceId));
        setCurrentWorkspace(remainingWorkspaces[0] || null);
      }

      toastT('toast.workspaceDeleted', 'success');
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      toastT('toast.deleteWorkspaceFailed', 'error');
      throw error;
    }
  };

  const switchWorkspace = (workspace) => {
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const isReselect =
      workspace?.id != null &&
      currentWorkspace?.id != null &&
      String(workspace.id) === String(currentWorkspace.id);

    snapshotHydratedRef.current = null;
    setCollections([]);
    setSchemas(normalizeSchemas(null));
    setCurrentWorkspace(workspace);
    setSelectedRequest(null);
    setSelectedCollection(null);
    // `null` = no workspace open: the project scope switched to a project that
    // has none (scope/ProjectScopeSync). Same path as a pick, so the open tabs
    // are saved for the outgoing workspace and closed (useTabPersistence).
    if (!workspace || isLocalWorkspace(workspace, isAuthenticated)) {
      setEnvironments([]);
      setSelectedEnvironment(null);
    }
    if (isReselect) {
      setWorkspaceReloadNonce((n) => n + 1);
    }
    if (workspace) toastT('toast.switchedWorkspace', 'success', { name: workspace.name });
  };

  /* ── Workspace modal ───────────────────────────────────────────────────── */
  const handleOpenWorkspaceModal = (mode = 'create', workspace = null) => {
    setWorkspaceModalMode(mode);
    setSelectedWorkspaceForEdit(workspace);
    setShowWorkspaceModal(true);
  };

  const handleWorkspaceCreated = async (workspaceData) => {
    try {
      await createWorkspace(workspaceData);
      setShowWorkspaceModal(false);
    } catch (error) {
      // Error is already handled in createWorkspace
    }
  };

  const handleWorkspaceUpdated = async (workspaceId, workspaceData) => {
    try {
      await editWorkspace(workspaceId, workspaceData);
      setShowWorkspaceModal(false);
      setSelectedWorkspaceForEdit(null);
    } catch (error) {
      // Error is already handled in editWorkspace
    }
  };

  const handleWorkspaceEdit = (workspace) => {
    handleOpenWorkspaceModal('edit', workspace);
  };

  const handleWorkspaceDelete = async (workspaceId) => {
    try {
      await deleteWorkspace(workspaceId);
    } catch (error) {
      // Error is already handled in deleteWorkspace
    }
  };

  return {
    getDefaultCloudWorkspace,
    canAccessWorkspace,
    reloadWorkspacesFromServer,
    createWorkspace,
    editWorkspace,
    deleteWorkspace,
    switchWorkspace,
    handleOpenWorkspaceModal,
    handleWorkspaceCreated,
    handleWorkspaceUpdated,
    handleWorkspaceEdit,
    handleWorkspaceDelete,
    handleGitSync,
    handleGitPull,
    handleGitChanges,
    handleGitPutBack,
    handleGitItemHistory,
    handleGitConnectConfirm,
    handleGitAddRemote,
    handleGitStopTracking,
    handleGitResolveConflicts,
    handleWorkspaceCloned,
    requestPublishLocalWorkspace,
    handlePublishLocalConfirm,
    canPublishToNetwork,
    requestPublishToNetwork,
    handleNetworkImported,
    openForkWorkspaceModal,
    handleForkWorkspaceConfirm,
  };
}
